import { useState, useEffect } from 'react';
import { userAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { TransactionFilters, applyTxFilters, EMPTY_TX_FILTERS } from '../components/TransactionFilters';
import {
  CreditCard,
  Receipt,
  CurrencyDollar,
  CalendarBlank,
  CircleNotch,
  ArrowDown,
  ArrowUp,
  FileText,
  Crown,
  Briefcase,
} from '@phosphor-icons/react';

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_TX_FILTERS);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await userAPI.getTransactions();
        setTransactions(response.data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const filtered = applyTxFilters(transactions, filters);
  const filtersActive = JSON.stringify(filters) !== JSON.stringify(EMPTY_TX_FILTERS);

  const totalSpent = filtered.reduce((sum, t) => sum + (t.amount || 0), 0);
  const subscriptionTotal = filtered.filter(t => t.type === 'subscription').reduce((sum, t) => sum + (t.amount || 0), 0);
  const commissionTotal = filtered.filter(t => t.type?.includes('commission')).reduce((sum, t) => sum + (t.amount || 0), 0);

  const getTypeInfo = (type) => {
    const map = {
      subscription: { label: 'Suscripción', icon: Crown, color: 'bg-purple-100 text-purple-600', badge: 'bg-purple-100 text-purple-700 border-purple-200' },
      commission_client: { label: 'Comisión Cliente', icon: Briefcase, color: 'bg-blue-100 text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
      commission_provider: { label: 'Comisión Proveedor', icon: FileText, color: 'bg-orange-100 text-orange-600', badge: 'bg-orange-100 text-orange-700 border-orange-200' },
    };
    return map[type] || { label: type || 'Otro', icon: CreditCard, color: 'bg-zinc-100 text-zinc-600', badge: 'bg-zinc-100 text-zinc-700 border-zinc-200' };
  };

  const getStatusInfo = (status) => {
    const map = {
      completed: { label: 'Completado', class: 'bg-green-100 text-green-700 border-green-200' },
      mocked: { label: 'Procesado', class: 'bg-green-100 text-green-700 border-green-200' },
      pending: { label: 'Pendiente', class: 'bg-amber-100 text-amber-700 border-amber-200' },
      failed: { label: 'Fallido', class: 'bg-red-100 text-red-700 border-red-200' },
    };
    return map[status] || { label: status || 'Desconocido', class: 'bg-zinc-100 text-zinc-700 border-zinc-200' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getDescription = (tx) => {
    if (tx.description) return tx.description;
    if (tx.type === 'commission_client') return `Confirmación de compra — ${tx.quotation_name || tx.quotation_category || 'Cotización'}`;
    if (tx.type === 'commission_provider') return `Comisión por venta — ${tx.quotation_name || tx.quotation_category || 'Cotización'}`;
    if (tx.type === 'subscription') return 'Suscripción Consultor de Precios';
    return 'Transacción';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" data-testid="transactions-loading" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Historial de Transacciones</h1>
        <p className="text-zinc-600 dark:text-zinc-400">Resumen de tus pagos, comisiones y suscripciones</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Total Transacciones</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{formatCurrency(totalSpent)}</p>
                <p className="text-xs text-zinc-500 mt-1">{filtered.length} transacciones</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                <CurrencyDollar className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Suscripciones</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{formatCurrency(subscriptionTotal)}</p>
                <p className="text-xs text-zinc-500 mt-1">{filtered.filter(t => t.type === 'subscription').length} pagos</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Comisiones</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{formatCurrency(commissionTotal)}</p>
                <p className="text-xs text-zinc-500 mt-1">{filtered.filter(t => t.type?.includes('commission')).length} pagos</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <TransactionFilters value={filters} onChange={setFilters} resultCount={filtered.length} />

      {/* Transactions List */}
      {filtered.length === 0 ? (
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">{filtersActive ? 'Sin resultados' : 'Sin transacciones'}</h3>
            <p className="text-zinc-500 max-w-sm mx-auto">
              {filtersActive
                ? 'Ninguna transacción coincide con los filtros.'
                : 'Aquí aparecerán tus pagos, comisiones y suscripciones.'}
            </p>
            {filtersActive && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-4 text-orange-600"
                onClick={() => setFilters(EMPTY_TX_FILTERS)}
                data-testid="show-all-transactions-btn"
              >
                Ver todas las transacciones
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="transactions-list">
          {filtered.map((tx) => {
            const typeInfo = getTypeInfo(tx.type);
            const statusInfo = getStatusInfo(tx.status);
            const TypeIcon = typeInfo.icon;

            return (
              <Card
                key={tx.transaction_id}
                className="border-zinc-200 rounded-none hover:shadow-[2px_2px_0px_0px_rgba(9,9,11,0.1)] transition-shadow"
                data-testid={`transaction-item-${tx.transaction_id}`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Icon */}
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center ${typeInfo.color}`}>
                      <TypeIcon className="w-4 h-4 sm:w-5 sm:h-5" weight="bold" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {getDescription(tx)}
                        </p>
                        <p className={`text-sm sm:text-base font-bold flex-shrink-0 ${tx.amount > 0 ? 'text-zinc-900 dark:text-zinc-100' : 'text-green-600'}`}>
                          {tx.amount > 0 ? '-' : ''}{formatCurrency(tx.amount)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Badge variant="outline" className={`rounded-none text-xs ${typeInfo.badge}`}>
                          {typeInfo.label}
                        </Badge>
                        <Badge variant="outline" className={`rounded-none text-xs ${statusInfo.class}`}>
                          {statusInfo.label}
                        </Badge>
                        <span className="text-xs text-zinc-400 flex items-center gap-1">
                          <CalendarBlank className="w-3 h-3" />
                          {formatDate(tx.created_at)}
                        </span>
                      </div>
                      {tx.subtotal && tx.iva ? (
                        <p className="text-xs text-zinc-400 mt-1">
                          Subtotal: {formatCurrency(tx.subtotal)} + IVA: {formatCurrency(tx.iva)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransactionsPage;
